import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import QueryKey from "@app/constants/QueryKeys.ts";
import { MissionsPage, MissionResponse } from "@app/models/Mission.ts";
import { useMissionService } from "@app/services/api/useMissionService.ts";

const stripAccents = (value: string) =>
  value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

export const useMissions = (
  page: number,
  size: number,
  clientSearch: string | undefined,
  userEmailSearch: string | undefined,
  showEndedMissions: boolean,
) => {
  const { fetchMissions } = useMissionService();

  const clientQuery = clientSearch?.trim().toLowerCase();
  const emailQuery = userEmailSearch?.trim().toLowerCase();

  const { data, error, isLoading } = useQuery({
    queryKey: [
      QueryKey.MISSIONS,
      clientQuery ?? undefined,
      emailQuery ?? undefined,
      showEndedMissions,
    ],
    queryFn: () => fetchMissions(),
  });

  const missions: MissionsPage | undefined = useMemo(() => {
    if (!data) {
      return undefined;
    }

    const filteredMissions = data.filter((mission: MissionResponse) => {
      const matchesClient =
        !clientQuery || mission.clientName.toLowerCase().includes(clientQuery);
      const user = mission.takimaUser;
      const matchesEmail =
        !emailQuery ||
        mission.takimaUserEmail.toLowerCase().includes(emailQuery) ||
        (!!user &&
          stripAccents(`${user.firstName} ${user.lastName}`).includes(
            stripAccents(emailQuery),
          ));

      const matchesEnded = showEndedMissions
        ? mission.missionEnded === true
        : true;

      return matchesClient && matchesEmail && matchesEnded;
    });

    const startIndex = (page - 1) * size;
    const content = filteredMissions.slice(startIndex, startIndex + size);

    return {
      content,
      totalElements: filteredMissions.length,
    };
  }, [data, page, size, clientQuery, emailQuery, showEndedMissions]);

  return {
    missions,
    error,
    isLoading,
  };
};
